import React, { useEffect, useRef, useState } from 'react';
import {
  MicrophoneIcon,
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  StopIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import InvoicePreview from '../billing/InvoicePreview';
import AssistantQuote from '../quotes/AssistantQuote';

// Building blocks for the assistant, used by the Assistant page and the
// quick-access drawer. Everything takes the object returned by useAssistant().

const TYPE = { theatre: 'Theatre list', rooms: 'Rooms', on_call: 'On call', leave: 'Leave' };
const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' });
const label = (key) => key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

// ---------------------------------------------------------------------------
// The scrolling conversation (with the empty state).
// ---------------------------------------------------------------------------
export const AssistantConversation = ({ assistant, admin, className = '', showCapabilities = false }) => {
  const { status, ready, turns, busy, send, choose, decide, suggestions } = assistant;
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns]);

  return (
    <div ref={scrollRef} className={`min-h-0 overflow-y-auto px-5 py-4 ${className}`}>
      {status && !ready && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {status.reason}
            {admin && <> <a href="/settings" className="font-semibold underline">Open Settings</a></>}
          </span>
        </div>
      )}

      {turns.length === 0 && (
        <div className="mx-auto max-w-xl py-8 text-center">
          <p className="text-base font-semibold text-slate-700">How can I help?</p>
          <p className="mt-1 text-sm text-slate-400">Speak or type. Questions are answered straight away; changes wait for your confirmation.</p>
          {suggestions.length > 0 && (
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  disabled={!ready || busy}
                  onClick={() => send(s)}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-brand-navy hover:text-brand-navy disabled:opacity-40"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          {showCapabilities && <div className="mt-6 text-left"><CapabilityList assistant={assistant} /></div>}
        </div>
      )}

      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        {turns.map((t) => <Turn key={t.id} turn={t} onDecide={decide} onChoose={choose} busy={busy} />)}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Mic + text box + send.
// ---------------------------------------------------------------------------
export const AssistantComposer = ({ assistant, placeholder = 'Ask or tell OmniPrac…' }) => {
  const { ready, busy, send, speech } = assistant;
  const [input, setInput] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!busy) inputRef.current?.focus();
  }, [busy]);

  const submit = async () => {
    const text = input;
    if (!text.trim()) return;
    setInput('');
    const sent = await send(text);
    if (!sent) setInput(text);
  };

  return (
    <div className="border-t border-slate-200 bg-white p-3">
      <div className="mx-auto max-w-3xl">
        {speech.error && <p className="mb-2 text-xs text-red-600">{speech.error}</p>}
        <div className="flex items-end gap-2">
          {speech.supported && (
            <button
              onClick={() => (speech.listening ? speech.stop() : speech.start())}
              disabled={!ready || busy}
              title={speech.listening ? 'Stop listening' : 'Tap and speak'}
              className={`grid h-11 w-11 shrink-0 place-items-center rounded-full text-white transition disabled:opacity-40 ${
                speech.listening ? 'animate-pulse bg-red-500' : 'bg-brand-gradient hover:opacity-95'
              }`}
            >
              {speech.listening ? <StopIcon className="h-5 w-5" /> : <MicrophoneIcon className="h-5 w-5" />}
            </button>
          )}
          <textarea
            ref={inputRef}
            rows={1}
            value={speech.listening ? speech.interim : input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            disabled={!ready || speech.listening}
            placeholder={speech.listening ? 'Listening… pause for 3 seconds or tap the mic when you’re done' : placeholder}
            className="max-h-32 min-h-[44px] flex-1 resize-none rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-teal/40 disabled:bg-slate-50"
          />
          <button
            onClick={submit}
            disabled={!ready || busy || !input.trim()}
            title="Send (Enter)"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand-navy text-white hover:bg-brand-deep disabled:opacity-40"
          >
            <PaperAirplaneIcon className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-400">
          <ShieldCheckIcon className="h-3.5 w-3.5 shrink-0" />
          The assistant can only run approved actions, and nothing changes until you confirm.
          {speech.supported ? ' Voice uses your browser’s speech recognition.' : ' Voice input needs Chrome or Edge.'}
        </p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// "What I can help with" — the enabled modules and their actions.
// ---------------------------------------------------------------------------
export const CapabilityList = ({ assistant }) => {
  const { status, ready, busy, send } = assistant;
  const modules = status?.modules || [];
  if (!modules.length) {
    return <p className="text-sm text-slate-400">{status === null ? 'Loading…' : 'Nothing is enabled for you yet.'}</p>;
  }
  return (
    <div className="flex flex-col gap-4">
      {modules.map((m) => (
        <div key={m.module} className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="font-semibold text-slate-800">{m.name}</h3>
          <p className="mt-0.5 text-xs text-slate-500">{m.description}</p>
          <ul className="mt-3 flex flex-col gap-2.5">
            {m.procedures.map((p) => (
              <li key={p.proc}>
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  {p.title}
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${p.kind === 'write' ? 'bg-amber-100 text-amber-700' : p.kind === 'workflow' ? 'bg-teal-100 text-teal-700' : 'bg-blue-100 text-blue-700'}`}>
                    {p.kind === 'write' ? 'asks to confirm' : p.kind === 'workflow' ? 'preview for you to check' : 'read only'}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {p.examples.map((ex) => (
                    <button
                      key={ex}
                      disabled={!ready || busy}
                      onClick={() => send(ex)}
                      className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-teal-50 hover:text-brand-navy disabled:opacity-40"
                    >
                      “{ex}”
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// One exchange.
// ---------------------------------------------------------------------------
const Turn = ({ turn, onDecide, onChoose, busy }) => {
  const r = turn.response;
  return (
    <div className="flex flex-col gap-2">
      <div className="ml-10 self-end rounded-2xl rounded-br-sm bg-brand-navy px-3.5 py-2 text-sm text-white">
        {turn.channel === 'voice' && <MicrophoneIcon className="mr-1 inline h-3.5 w-3.5 opacity-70" />}
        {turn.text}
      </div>

      <div className="mr-6 self-start">
        {turn.pending && (
          <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-sm bg-white px-3.5 py-2.5 shadow-sm">
            {/^\s*(?:ok(?:ay)?[\s,.!]+)?(?:please\s+)?(?:bill|code)\b.{6,}/i.test(turn.text) && <span className="mr-1 text-xs text-slate-500">Finding ICD-10 codes — this takes about half a minute</span>}
            {/^\s*(?:ok(?:ay)?[\s,.!]+)?(?:please\s+)?(?:make\s+a\s+)?(?:quote|estimate)\b/i.test(turn.text) && <span className="mr-1 text-xs text-slate-500">Working out the estimate</span>}
            {[0, 150, 300].map((d) => (
              <span key={d} className="h-2 w-2 animate-bounce rounded-full bg-brand-teal" style={{ animationDelay: `${d}ms` }} />
            ))}
          </div>
        )}

        {turn.error && <div className="rounded-2xl rounded-bl-sm border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">{turn.error}</div>}

        {r && (
          <div className="flex flex-col gap-2">
            <div className="rounded-2xl rounded-bl-sm bg-white px-3.5 py-2.5 text-sm text-slate-700 shadow-sm">{r.reply}</div>
            {r.reads?.map((read, i) => <ResultView key={`${read.proc}-${i}`} read={read} />)}
            {r.error && (
              <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{r.error}</span>
              </div>
            )}
            {r.proposal && <ProposalCard turn={turn} onDecide={onDecide} />}
            {r.draft && <InvoicePreview draft={r.draft} />}
            {r.choices?.length > 0 && (
              <div className="flex flex-col gap-1 rounded-xl border border-slate-200 bg-white p-1.5 shadow-sm">
                {r.choices.map((c) => (
                  <button
                    key={`${c.context.bid}-${c.context.patient_id}`}
                    onClick={() => onChoose(c)}
                    disabled={busy}
                    className="rounded-lg px-3 py-2 text-left hover:bg-teal-50 disabled:opacity-50"
                  >
                    <div className="text-sm font-semibold text-slate-800">{c.label}</div>
                    <div className="text-xs text-slate-500">{c.detail}</div>
                  </button>
                ))}
              </div>
            )}
            {r.quote && <AssistantQuote quote={r.quote.quote} estimate={r.quote.estimate} />}
            {r.meta && (
              <p className="px-1 text-[10px] text-slate-400">
                {r.meta.model} · {(r.meta.duration_ms / 1000).toFixed(1)}s
                {r.meta.cost != null && ` · ${r.meta.currency} ${r.meta.cost.toFixed(4)}`}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Results of a read procedure. Known shapes get a tailored view; anything else
// (a future module) falls back to a simple table of its first list.
const ResultView = ({ read }) => {
  const known = Array.isArray(read.sessions) || Array.isArray(read.free);
  const listKey = !known && Object.keys(read).find((k) => Array.isArray(read[k]) && read[k].every((x) => x && typeof x === 'object'));
  const list = listKey ? read[listKey] : null;
  const cols = list?.length ? Object.keys(list[0]).filter((k) => !/_id$|_uid$/.test(k)).slice(0, 6) : [];

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {read.summary || read.title}
      </div>

      {Array.isArray(read.sessions) && (read.sessions.length === 0 ? (
        <p className="px-3 py-2 text-sm text-slate-400">Nothing scheduled.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {read.sessions.map((s) => (
            <li key={s.session_id} className="flex gap-3 px-3 py-2 text-sm">
              <span className="w-24 shrink-0">
                <span className="block font-semibold text-brand-navy">{fmtDate(s.date)}</span>
                <span className="block text-xs text-slate-500">{s.start ? `${s.start}–${s.end}` : 'All day'}</span>
              </span>
              <span className="min-w-0">
                <span className="block font-medium text-slate-700">{TYPE[s.type]}{s.hospital ? ` · ${s.hospital}` : ''}</span>
                <span className="block truncate text-xs text-slate-500">{[s.doctor, s.theatre, s.surgeon].filter(Boolean).join(' · ')}</span>
              </span>
            </li>
          ))}
        </ul>
      ))}

      {Array.isArray(read.free) && (
        <p className="px-3 py-2 text-sm text-slate-700">{read.free.length ? read.free.map((f) => f.doctor).join(', ') : 'Nobody is completely free that day.'}</p>
      )}

      {list && (list.length === 0 ? (
        <p className="px-3 py-2 text-sm text-slate-400">Nothing found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase text-slate-400">
                {cols.map((c) => <th key={c} className="px-3 py-1.5 font-semibold">{label(c)}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.slice(0, 50).map((row, i) => (
                <tr key={i}>{cols.map((c) => <td key={c} className="px-3 py-1.5 text-slate-700">{row[c] == null ? '—' : String(row[c])}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

const ProposalCard = ({ turn, onDecide }) => {
  const { proposal } = turn.response;
  const steps = turn.outcome || proposal.steps;
  const done = turn.decision === 'confirmed';
  const cancelled = turn.decision === 'cancelled';
  return (
    <div className={`overflow-hidden rounded-xl border-2 bg-white ${done ? 'border-green-300' : cancelled ? 'border-slate-200' : 'border-amber-300'}`}>
      <div className={`px-3 py-2 text-xs font-bold uppercase tracking-wide ${done ? 'bg-green-50 text-green-700' : cancelled ? 'bg-slate-50 text-slate-400' : 'bg-amber-50 text-amber-800'}`}>
        {done ? 'Done' : cancelled ? 'Cancelled — nothing changed' : `Confirm ${steps.length === 1 ? 'this change' : `these ${steps.length} changes`}`}
      </div>
      <ol className="divide-y divide-slate-100">
        {steps.map((s, i) => (
          <li key={i} className="px-3 py-2">
            <div className={`flex items-start gap-2 text-sm ${cancelled ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
              {done ? <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-green-600" /> : <span className="mt-0.5 w-4 shrink-0 text-xs font-bold text-slate-400">{i + 1}.</span>}
              <span>{s.summary}</span>
            </div>
            {s.warnings?.map((w) => (
              <div key={w} className="ml-6 mt-1 flex items-start gap-1 text-xs text-amber-700">
                <ExclamationTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {w}
              </div>
            ))}
          </li>
        ))}
      </ol>
      {turn.decision === 'failed' && <p className="border-t border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">{turn.decisionError}</p>}
      {!turn.decision && (
        <div className="flex justify-end gap-2 border-t border-slate-100 px-3 py-2">
          <button onClick={() => onDecide(turn, false)} disabled={turn.deciding} className="rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={() => onDecide(turn, true)} disabled={turn.deciding} className="rounded-lg bg-brand-gradient px-4 py-1.5 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-60">
            {turn.deciding ? 'Applying…' : 'Confirm'}
          </button>
        </div>
      )}
    </div>
  );
};
